/**
 * TopluAnalizPage - Bulk Analysis Page
 * 
 * Birden fazla influencer'ı aynı anda analiz etme sayfası
 * Sonuçlar Excel olarak email ile gönderilir
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { getDefaultCurrency as getDefaultCurrencyForLocale } from '@/utils/currency';
import { isPersonaStaff } from '@/utils/staff';
import NavTabs from '../components/NavTabs';
import Footer from '../components/Footer';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Users, FileSpreadsheet, Loader2, CheckCircle, Plus, AlertCircle, ClipboardList, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const MAX_INFLUENCERS = 50;
const INITIAL_INFLUENCERS = 10;

// Currency options
const CURRENCIES = [
  'USD', 'EUR', 'GBP', 'TRY', 'SAR', 'AED', 'JPY', 'CNY', 'INR', 'AUD',
  'CAD', 'CHF', 'KRW', 'BRL', 'MXN', 'RUB', 'SGD', 'HKD', 'SEK', 'NOK'
];

export default function TopluAnalizPage() {
  const navigate = useNavigate();
  const { user, refreshUserData, loading: authLoading } = useAuth();
  const { t, locale } = useTranslation();
  
  // Get default currency based on locale
  const getDefaultCurrency = () => getDefaultCurrencyForLocale(locale);
  
  // Currency state
  const [currency, setCurrency] = useState(getDefaultCurrency());
  
  // Helper: Get locale-aware paths for navigation
  const getLocalePath = (route) => {
    const routes = {
      en: { login: '/en/login', history: '/en/history' },
      tr: { login: '/tr/giris', history: '/tr/analizgecmisi' },
      ar: { login: '/ar/login', history: '/ar/history' },
      fr: { login: '/fr/login', history: '/fr/history' },
      de: { login: '/de/login', history: '/de/history' },
      es: { login: '/es/login', history: '/es/history' }
    };
    return routes[locale]?.[route] || routes.en[route];
  };
  
  // Initialize with 10 influencer objects (username + optional payment + currency)
  const [influencers, setInfluencers] = useState(
    Array.from({ length: INITIAL_INFLUENCERS }, () => ({ username: '', payment: '', currency: getDefaultCurrency() }))
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pasteText, setPasteText] = useState('');  // Excel-paste: "Listeni Kopyala" akordiyonu

  // Redirect if not logged in
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(getLocalePath('login'));
    }
  }, [user, navigate, locale, authLoading]);

  // Count filled influencers
  const filledInfluencers = influencers.filter(inf => inf.username.trim().length > 0);
  const influencerCount = filledInfluencers.length;

  // Handle username change for specific index
  const handleUsernameChange = (index, value) => {
    const newInfluencers = [...influencers];
    // Remove @ symbol if user types it
    newInfluencers[index].username = value.replace('@', '').trim();
    setInfluencers(newInfluencers);
  };

  // Handle payment change for specific index
  const handlePaymentChange = (index, value) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    const newInfluencers = [...influencers];
    newInfluencers[index].payment = numericValue;
    setInfluencers(newInfluencers);
  };

  // Handle currency change - changes ALL rows to same currency
  const handleCurrencyChange = (index, value) => {
    const newInfluencers = influencers.map(inf => ({
      ...inf,
      currency: value
    }));
    setInfluencers(newInfluencers);
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US').format(parseInt(value));
  };

  // Excel/Word-paste parser: AYRACA degil TOKEN-TIPINE bakar (fiyat=bastan-sona rakam; username=harf-iceren).
  // Excel=TAB, Word=coklu/tek bosluk/yanyana, liste=virgul -> hepsi calisir. Donus: [{username, payment}].
  // NOT: virgul/nokta sayi-ici BINLIK ayraci olarak KALIR (185,000 tek token); token bas/son virgulu strip'lenir
  //      (liste "user1, user2" icin) -> ayrac yalniz TAB / ; / whitespace.
  const HEADER_TOKENS = new Set([
    'instagram', 'kullanici', 'adi', 'persona', 'teklif', 'ettigi', 'tutar',
    'username', 'price', 'payment', 'fiyat', 'name', 'reel'
  ]);
  const parsePastedRows = (text) => {
    if (!text) return [];
    // Excel tirnak-ici-TAB/newline sarar ("emineozdemir.blog<TAB>"<TAB>5,000) -> TUM tirnaklari GLOBAL temizle.
    const cleaned = String(text).replace(/["']/g, '');
    const rows = [];
    const seen = new Set();
    for (const line of cleaned.split(/\r?\n/)) {
      const tokens = line.split(/[\t;]+|\s+/).filter((tk) => tk.length > 0);
      for (let tk of tokens) {
        tk = tk.replace(/^[,;]+|[,;]+$/g, '');  // bas/son virgul-noktalivirgul strip (liste ayraci kalintisi)
        if (!tk) continue;
        // SAF-FIYAT: bastan sona rakam + . , (185,000 / 1.000 / 37500). Harf-iceren HAYIR (username).
        if (/^[0-9][0-9.,]*$/.test(tk)) {
          const digits = tk.replace(/[^0-9]/g, '');
          if (digits && rows.length > 0 && !rows[rows.length - 1].payment) {
            rows[rows.length - 1].payment = digits;  // en-son-username'e ata (bos ise; ezme yok)
          }
          continue;
        }
        // USERNAME: @ strip + lowercase; gecerli handle (^[a-z0-9._]+$ & len<=30); header/gecersiz -> atla.
        const u = tk.replace(/@/g, '').trim().toLowerCase();
        if (!u || u.length > 30) continue;
        if (HEADER_TOKENS.has(u)) continue;         // header kelimesi (instagram/persona/teklif/tutar...)
        if (!/^[a-z0-9._]+$/.test(u)) continue;     // Turkce/bosluk/gecersiz karakter -> atla
        if (seen.has(u)) continue;                  // dedupe (cakisan ILK fiyati korur)
        seen.add(u);
        rows.push({ username: u, payment: '' });
      }
    }
    return rows;
  };

  // Canli algilanan sayim (textarea altinda hint)
  const pasteDetectedCount = parsePastedRows(pasteText).length;

  // "Tabloya dagit": yapistirilan isimleri ilk bos kutulara dagit (MAX 50 HARD-cap; backend max_length=50)
  const distributePastedUsernames = () => {
    const parsedRows = parsePastedRows(pasteText);
    if (parsedRows.length === 0) {
      toast.error(t('bulk.paste_no_valid'));
      return;
    }
    const next = influencers.map(inf => ({ ...inf }));
    // Mevcut dolu username'ler (duplikasyon icin, lowercase + @-strip)
    const existing = new Set(
      next.filter(inf => inf.username.trim().length > 0)
          .map(inf => inf.username.trim().replace(/@/g, '').toLowerCase())
    );
    let added = 0, dup = 0, overflow = 0;
    for (const row of parsedRows) {
      if (existing.has(row.username)) { dup++; continue; }
      let slot = next.findIndex(inf => inf.username.trim().length === 0);
      if (slot === -1) {
        if (next.length >= MAX_INFLUENCERS) { overflow++; continue; }  // 50 HARD-cap
        next.push({ username: '', payment: '', currency: getDefaultCurrency() });
        slot = next.length - 1;
      }
      next[slot].username = row.username;
      if (row.payment) next[slot].payment = row.payment;  // bos-fiyat EZME; currency DOKUNMA
      existing.add(row.username);
      added++;
    }
    if (added > 0) {
      setInfluencers(next);
      setPasteText('');
      toast.success(
        t('bulk.paste_result')
          .replace('{added}', added)
          .replace('{dup}', dup)
          .replace('{overflow}', overflow)
      );
    } else {
      toast.error(t('bulk.paste_no_valid'));  // hepsi dup/overflow
    }
  };

  // Parse valid influencers
  const parseInfluencers = () => {
    const mapped = influencers
      .filter(inf => inf.username.trim().length > 0 && inf.username.trim().length <= 30)
      .map(inf => ({
        username: inf.username.trim().replace('@', '').toLowerCase(),
        payment: inf.payment ? parseInt(inf.payment) : null,
        currency: inf.currency || getDefaultCurrency()
      }));
    // Dedupe (case-insensitive; username already @-stripped + lowercased above)
    const seen = new Set();
    return mapped.filter(inf => {
      if (seen.has(inf.username)) return false;
      seen.add(inf.username);
      return true;
    });
  };

  // Add new influencer row
  const addInfluencer = () => {
    if (influencers.length < MAX_INFLUENCERS) {
      setInfluencers([...influencers, { username: '', payment: '', currency: getDefaultCurrency() }]);
    } else {
      toast.error(t('bulk.max_error').replace('{max}', MAX_INFLUENCERS));
    }
  };

  // Calculate cost
  const totalCost = influencerCount + 1; // Influencer sayısı + 1 raporlama primi

  // Check if user has enough credits
  const hasEnoughCredits = isPersonaStaff(user) || (user?.credits_remaining || 0) >= totalCost;

  // Handle submit
  const handleSubmit = () => {
    if (influencerCount === 0) {
      toast.error(t('bulk.min_error'));
      return;
    }
    if (!hasEnoughCredits) {
      toast.error(t('bulk.credits_error'));
      return;
    }
    // Detect duplicate usernames (case-insensitive, @-stripped) and warn before proceeding
    const seenNames = new Set();
    const duplicates = [];
    for (const inf of filledInfluencers) {
      const key = inf.username.replace(/^@/, '').toLowerCase().trim();
      if (seenNames.has(key)) {
        duplicates.push(inf.username.trim());
        continue;
      }
      seenNames.add(key);
    }
    if (duplicates.length > 0) {
      const msg = t('bulk.duplicate_warning')
        .replace('{count}', duplicates.length)
        .replace('{list}', duplicates.join(', '));
      if (!window.confirm(msg)) {
        return;
      }
    }
    setShowConfirmModal(true);
  };

  // Confirm and start analysis
  const confirmAnalysis = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      // Use auth_token from localStorage (set by AuthContext)
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        toast.error(t('bulk.generic_error'));
        console.error('No auth_token found in localStorage');
        setIsLoading(false);
        return;
      }
      
      const validInfluencers = parseInfluencers();

      const response = await axios.post(
        `${API}/api/bulk-analyze`,
        {
          influencers: validInfluencers,
          lang: locale  // i18n: oturum dili -> bulk request (C1 BulkAnalyzeRequest.lang); tekil useAnalyzeReel:480 simetrigi
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setShowSuccessModal(true);
        // Reset all inputs to initial 10
        setInfluencers(Array.from({ length: INITIAL_INFLUENCERS }, () => ({ username: '', payment: '', currency: getDefaultCurrency() })));
        // Refresh user data to update credits
        if (refreshUserData) {
          await refreshUserData();
        }
      } else {
        toast.error(response.data.error || t('bulk.generic_error'));
      }
    } catch (error) {
      console.error('Bulk analysis error:', error);
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 400 && /unique/i.test(detail)) {
        toast.error(t('bulk.no_unique_influencers'));
      } else {
        toast.error(detail || t('bulk.analysis_error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Navigation */}
      <NavTabs user={user} currentPage="topluanaliz" />

      {/* Content wrapper with flex-1 to push footer down */}
      <div className="flex-1">
        {/* Main Content */}
        <main className="container mx-auto px-4 pb-16 max-w-[50rem]">
          {/* Başlık - Kutunun dışında */}
          <div className="text-center mt-8 mb-4">
            <div className="flex items-center justify-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg flex-shrink-0">
                <Users className="w-3 h-3 text-white" />
              </div>
              <p className="text-orange-500" style={{ fontSize: '17px' }}>
                {t('bulk.subtitle')}
              </p>
            </div>
            <p className="font-bold text-white" style={{ fontSize: '15px' }}>
              {user?.email || ''}
            </p>
          </div>

          <Card className="bg-gray-900 border-2 border-orange-500">
            <CardContent className="space-y-4 pt-4">
              {/* Excel-paste akordiyonu: "Listeni Kopyala" - tabloya otomatik dagit */}
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="paste" className="border border-orange-500/30 rounded-md px-3">
                  <AccordionTrigger className="text-orange-400 hover:text-orange-300 hover:no-underline" style={{ fontSize: '14px' }}>
                    <span className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      {t('bulk.paste_title')}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-gray-400 pb-2" style={{ fontSize: '13px' }}>
                      {t('bulk.paste_desc')}
                    </p>
                    <textarea
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      placeholder={t('bulk.paste_placeholder')}
                      rows={3}
                      className="w-full resize-y bg-gray-800 border border-gray-700 rounded-md text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors px-2 py-1.5"
                      style={{ fontSize: '13px' }}
                      disabled={isLoading}
                      data-testid="bulk-paste-textarea"
                    />
                    <div className="flex items-center justify-between gap-2 pt-2">
                      <span className="text-gray-500" style={{ fontSize: '12px' }}>
                        {t('bulk.paste_detected').replace('{count}', pasteDetectedCount)}
                      </span>
                      <Button
                        onClick={distributePastedUsernames}
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        style={{ fontSize: '13px' }}
                        disabled={isLoading || pasteDetectedCount === 0}
                        data-testid="bulk-paste-distribute-btn"
                      >
                        <ArrowDown className="w-4 h-4 mr-1" />
                        {t('bulk.paste_distribute_btn')}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Input List - Each row has username + optional payment */}
              <div className="space-y-2">
                {influencers.map((influencer, index) => (
                <div key={index} className="flex items-center justify-center gap-1.5 md:gap-2">
                  <span className="text-gray-500 w-4 md:w-5 text-right flex-shrink-0" style={{ fontSize: '13px' }}>{index + 1}.</span>
                  <div className="flex gap-1.5 md:gap-2">
                    {/* Username Input - narrower */}
                    <input
                      type="text"
                      value={influencer.username}
                      onChange={(e) => handleUsernameChange(index, e.target.value)}
                      placeholder={t('bulk.username_placeholder')}
                      className="w-44 md:w-52 h-8 px-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
                      style={{ fontSize: '14px' }}
                      disabled={isLoading}
                      data-testid={`username-input-${index}`}
                    />
                    {/* Payment Input */}
                    <input
                      type="text"
                      value={formatCurrency(influencer.payment)}
                      onChange={(e) => handlePaymentChange(index, e.target.value.replace(/,/g, ''))}
                      placeholder={t('bulk.payment_placeholder')}
                      className="w-28 md:w-32 h-8 px-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
                      style={{ fontSize: '14px' }}
                      disabled={isLoading}
                      data-testid={`payment-input-${index}`}
                    />
                    {/* Currency - Dropdown only on first row, text on others */}
                    {index === 0 ? (
                      <select
                        value={influencer.currency}
                        onChange={(e) => handleCurrencyChange(index, e.target.value)}
                        className="w-16 md:w-18 h-8 px-1 bg-gray-800 border border-gray-700 rounded-md text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
                        style={{ fontSize: '13px' }}
                        disabled={isLoading}
                        data-testid={`currency-select-${index}`}
                      >
                        {CURRENCIES.map(curr => (
                          <option key={curr} value={curr}>{curr}</option>
                        ))}
                      </select>
                    ) : (
                      <span 
                        className="w-16 md:w-18 h-8 px-1 flex items-center justify-center text-gray-400"
                        style={{ fontSize: '13px' }}
                      >
                        {influencer.currency}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Add Influencer Button + Counter - Same row */}
              <div className="flex items-center justify-center gap-4 pt-2">
                {/* Add Influencer Button - Centered, show if less than 20 */}
                {influencers.length < MAX_INFLUENCERS && (
                  <Button
                    onClick={addInfluencer}
                    variant="outline"
                    size="sm"
                    className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 hover:border-orange-500 hover:text-white"
                    style={{ fontSize: '14px' }}
                    disabled={isLoading}
                    data-testid="add-influencer-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('bulk.add_influencer')}
                  </Button>
                )}
                
                {/* Counter */}
                <span className={`font-medium ${
                  influencerCount > 0 ? 'text-orange-400' : 'text-gray-500'
                }`} style={{ fontSize: '13px' }}>
                  {t('bulk.influencers_added').replace('{count}', influencerCount).replace('{max}', MAX_INFLUENCERS)}
                </span>
              </div>
            </div>

            {/* Insufficient credits warning - only show if not enough */}
            {influencerCount > 0 && !hasEnoughCredits && (
              <div className="bg-red-900/20 border border-red-700 rounded-md p-3">
                <p className="text-red-400" style={{ fontSize: '14px' }}>
                  {t('bulk.insufficient_credits_warning').replace('{available}', user?.credits_remaining || 0)}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={isLoading || influencerCount === 0 || influencerCount > MAX_INFLUENCERS || !hasEnoughCredits}
              className="w-full h-11 font-bold bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
              style={{ fontSize: '15px' }}
              data-testid="bulk-analyze-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('bulk.processing')}
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  {t('bulk.submit_btn')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
        </main>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-900 border border-gray-700 max-w-md w-full">
            <CardContent className="pt-6 space-y-4">
              {/* Cost deduction message */}
              <p className="text-gray-300 text-center" style={{ fontSize: '15px' }}>
                <span className="font-bold text-white">{totalCost} {t('bulk.credits')}</span> {t('bulk.will_be_deducted')}
                <br />
                <span className="text-gray-400" style={{ fontSize: '14px' }}>({influencerCount} {t('bulk.influencers_count')} + 1 {t('bulk.reporting_fee')}).</span>
              </p>
              
              {/* Email notification message */}
              <p className="text-gray-300 text-center" style={{ fontSize: '15px' }}>
                {t('bulk.email_notification')} <span className="font-bold text-white">support@opara.co</span>.
              </p>
              
              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => setShowConfirmModal(false)}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                  style={{ fontSize: '14px' }}
                >
                  {t('bulk.cancel')}
                </Button>
                <Button
                  onClick={confirmAnalysis}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  style={{ fontSize: '14px' }}
                >
                  {t('bulk.start_analysis')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-900 border border-green-700 max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-green-500 flex items-center gap-2" style={{ fontSize: '20px' }}>
                <CheckCircle className="w-6 h-6" />
                {t('bulk.success_title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300" style={{ fontSize: '15px' }}>
                {t('bulk.success_message')}
              </p>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(getLocalePath('home'));
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                style={{ fontSize: '14px' }}
              >
                {t('bulk.go_to_homepage')}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}
